/**
 * Runner sets in the basket.
 *
 * snippets/cart-set-map.liquid shows a runner and the tape or underlay bought with it as one
 * card ([data-rr-set]). This file keeps the set together when the shopper changes it:
 *  - the card's bin ([data-rr-keys]) removes the runner and its add-ons in one request,
 *  - changing the runner's quantity moves its add-ons with it, in the same request.
 *
 * Both listeners run in the capture phase so they act before the theme's own remove and
 * quantity handlers. If this file fails to load, the bin falls back to the theme's usual
 * remove (runner and tape) and the quantity box changes the runner only.
 */
import { ThemeBus } from "@theme/events-bus";
import { EVENTS } from "@theme/event-constants";

if (!window.__rrBasket) {
  window.__rrBasket = true;

  const cartUrl = (path) => (window.Shopify?.routes?.root || "/").replace(/\/?$/, "/") + path;
  let queue = Promise.resolve();

  const send = (card, updates) => {
    queue = queue.then(async () => {
      card?.setAttribute("aria-busy", "true");
      const release = setTimeout(() => card?.removeAttribute("aria-busy"), 8000);
      try {
        const res = await fetch(cartUrl("cart/update.js"), {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ updates }),
        });
        if (res.ok) window.__cart = await res.json();
        else console.error("[rr-basket] cart update failed", res.status);
      } catch (err) {
        console.error("[rr-basket] cart update failed", err);
      }
      clearTimeout(release);
      card?.removeAttribute("aria-busy");
      // Re-render either way, so the basket always shows what the cart really holds.
      ThemeBus.dispatchEvent(new Event(EVENTS.cartUpdated));
    });
    return queue;
  };

  /* One bin for the whole set. */
  window.addEventListener(
    "click",
    (e) => {
      const button = e.target instanceof Element ? e.target.closest("[data-rr-keys]") : null;
      if (!button) return;
      const keys = String(button.dataset.rrKeys || "")
        .split(",")
        .map((k) => k.trim())
        .filter(Boolean);
      if (!keys.length) return;
      e.preventDefault();
      e.stopPropagation();
      send(button.closest("[data-rr-set]"), Object.fromEntries(keys.map((k) => [k, 0])));
    },
    true,
  );

  /* One quantity for the whole set: add-ons keep their ratio to the runner (1 tape per runner). */
  window.addEventListener(
    "change",
    (e) => {
      const input = e.target;
      if (!(input instanceof HTMLInputElement) || input.name !== "updates[]") return;
      const card = input.closest("[data-rr-set]");
      const parentKey = card?.dataset.rrParent;
      if (!parentKey) return;
      e.stopPropagation();
      const qty = Math.max(0, parseInt(input.value, 10) || 0);
      const was = Math.max(1, parseInt(card.dataset.rrQty, 10) || 1);
      const updates = { [parentKey]: qty };
      card.querySelectorAll("[data-rr-addon-key]").forEach((row) => {
        const key = row.dataset.rrAddonKey;
        const has = parseInt(row.dataset.rrAddonQty, 10) || 0;
        if (!key || !has) return;
        if (qty === 0) updates[key] = 0;
        else if (has % was === 0) updates[key] = (has / was) * qty;
      });
      send(card, updates);
    },
    true,
  );
}
